import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { MaskWithStructures } from "@/types/mask";
import SpecsSelectTable from "@/components/shared/SpecsSelectTable";
import { Loader } from "@/components/ui/loader";
import { SpecList } from "@/types/specList";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { MessageSquareWarning } from "lucide-react";
import { useRef, useMemo, useEffect } from "react";

type SpecTableWrapperProps = {
  linkedSpecIds: string[];
  maskWithStructures: MaskWithStructures | null;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  rowSelectionState: RowSelectionState;
  specListId: string;
  specLists: SpecList[];
  supportedRecipes: string[];
  isLoading: boolean;
};
export function SpecsTableWrapper({
  linkedSpecIds,
  maskWithStructures,
  onRowSelectionChange,
  rowSelectionState,
  specListId,
  specLists,
  isLoading,
  supportedRecipes,
}: SpecTableWrapperProps) {
  const previousSpecListIdRef = useRef<string | null>(null);

  const specList = useMemo(() => {
    if (specListId) {
      return specLists.find((sl) => sl.id === specListId);
    }

    return null;
  }, [specListId, specLists]);

  const specData = useMemo(() => {
    const structureNames = maskWithStructures?.structures.map((s) => s.name) ?? [];

    if (specList) {
      const filteredSpecs = specList.latestVersion.specValues.filter(
        (specValue) =>
          structureNames.includes(specValue.spec.name) ||
          structureNames.includes(specValue.spec.reportName) ||
          structureNames.includes(specValue.spec.dutNames) ||
          structureNames.includes(specValue.spec.legacyNames),
      );

      const specsFilteredOnRecipe = filteredSpecs.filter(
        (s) => s.spec.recipe && supportedRecipes.includes(s.spec.recipe.id),
      );

      return specsFilteredOnRecipe.map((s) => s.spec).flat();
    }

    return [];
  }, [maskWithStructures?.structures, specList, supportedRecipes]);

  useEffect(() => {
    if (specListId !== previousSpecListIdRef.current) {
      const initialState = linkedSpecIds.reduce(
        (acc, cur) => {
          acc[cur] = true;

          return acc;
        },
        {} as Record<string, boolean>,
      );
      onRowSelectionChange(initialState);
      previousSpecListIdRef.current = specListId;
    }
  }, [linkedSpecIds, specData, onRowSelectionChange, specListId]);

  if (isLoading) {
    return <Loader text="Loading specs..." />;
  }

  if (specData.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <MessageSquareWarning />
          </EmptyMedia>
          <EmptyTitle>No results.</EmptyTitle>
          <EmptyDescription>Try selecting a different measurement capability.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <SpecsSelectTable
      onRowSelectionChange={onRowSelectionChange}
      rowSelectionState={rowSelectionState}
      specData={specData}
    />
  );
}
